import { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { supabase } from '@/integrations/supabase/client';
import { Session } from '@supabase/supabase-js';
import { ThemeProvider } from './components/ThemeProvider';
import { LanguageProvider } from './contexts/LanguageContext';
import { RoleProvider, useRole } from './contexts/RoleContext';
import { UserProfile } from './types';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import { useSessionSecurity } from './hooks/useSessionSecurity';
import { useIsMobile } from './hooks/use-mobile';
import ErrorBoundary from './components/ErrorBoundary';
import { SessionExpiryWarning } from './components/ui/SessionExpiryWarning';
import { SoundProvider } from './contexts/SoundContext';
import { SoundToggle } from './components/ui/SoundToggle';

// Lazy load ALL heavy components for maximum code splitting
const AuthPage = lazy(() => import('./components/AuthPage'));
const AppSidebar = lazy(() => import('./components/AppSidebar'));
const CustomCursor = lazy(() => import('./components/ui/CustomCursor'));
const FilmGrain = lazy(() => import('./components/ui/FilmGrain'));
const PageTransition = lazy(() => import('./components/ui/PageTransition'));
const ScrollToTop = lazy(() => import('./components/ui/ScrollToTop').then(m => ({ default: m.ScrollToTop })));
const AnimatePresence = lazy(() => import('framer-motion').then(m => ({ default: m.AnimatePresence })));

// Lazy load route components
const Dashboard = lazy(() => import('./components/Dashboard'));
const OperationsCenter = lazy(() => import('./components/OperationsCenter'));
const FleetCommandPage = lazy(() => import('./components/FleetCommandPage'));
const FleetModule = lazy(() => import('./components/FleetModule'));
const UserProfileView = lazy(() => import('./components/UserProfileView'));
const MessengerPage = lazy(() => import('./components/messenger/MessengerPage').then(m => ({ default: m.MessengerPage })));
const VacationPage = lazy(() => import('./components/vacation/VacationPage').then(m => ({ default: m.VacationPage })));
const LeaveApprovalsPage = lazy(() => import('./components/approvals/LeaveApprovalsPage').then(m => ({ default: m.LeaveApprovalsPage })));
const CompanyDocsModule = lazy(() => import('./components/CompanyDocsModule'));
const PortalUsersModule = lazy(() => import('./components/PortalUsersModule'));
const ActivityLogPage = lazy(() => import('./components/ActivityLogPage'));
const MonthlyPOPage = lazy(() => import('./components/monthly-po/MonthlyPOPage'));
const TimesheetsPage = lazy(() => import('./components/timesheets/TimesheetsPage'));
const DailyTasksPage = lazy(() => import('./components/daily-tasks/DailyTasksPage'));
const EmployeeSelfServicePage = lazy(() => import('./components/employee/EmployeeSelfServicePage').then(m => ({ default: m.EmployeeSelfServicePage })));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const HomePage = lazy(() => import('./pages/HomePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const ChatWidget = lazy(() => import('./components/ai-helpdesk/ChatWidget'));
const PageBreadcrumb = lazy(() => import('./components/ui/PageBreadcrumb'));
const ShiftCalendar = lazy(() => import('./components/shifts/ShiftCalendar'));
const AnnouncementBoard = lazy(() => import('./components/announcements/AnnouncementBoard'));
const NurseDocumentsManagement = lazy(() => import('./components/employee/NurseDocumentsManagement'));
const DoctorDocumentsManagement = lazy(() => import('./components/employee/DoctorDocumentsManagement'));
const DriverDocumentsManagement = lazy(() => import('./components/employee/DriverDocumentsManagement'));
const ReportsPage = lazy(() => import('./components/reports/ReportsPage'));
const ProcurementPage = lazy(() => import('./components/procurement/ProcurementPage'));
const WarehousePage = lazy(() => import('./components/warehouse/WarehousePage'));
const SalesPage = lazy(() => import('./components/sales/SalesPage'));
const HRPage = lazy(() => import('./components/hr/HRPage'));
const CommandPalette = lazy(() => import('./components/CommandPalette'));

// Valid view names for URL mapping
const validViews = [
  'dashboard', 'operations', 'daily-tasks', 'monthly-po', 'fleet-tracker', 'fleet',
  'timesheets', 'company-info', 'portals', 'activity-log', 'messenger',
  'vacation', 'approvals', 'profile', 'self-service', 'shifts', 'announcements', 'nurse-docs', 'doctor-docs', 'driver-docs', 'reports', 'procurement', 'warehouse', 'sales', 'hr'
];

// Get view from URL path
const getViewFromPath = (pathname: string): string | null => {
  const path = pathname.replace(/^\//, '').split('/')[0];
  if (path && validViews.includes(path)) {
    return path;
  }
  return null;
};

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="relative">
      <div className="h-10 w-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
    </div>
  </div>
);

// Inner component that uses RoleContext
function AppContent({ 
  session, 
  userProfile, 
  setUserProfile,
  currentView,
  setCurrentView,
  sidebarCollapsed,
  setSidebarCollapsed,
  currentSessionId,
  handleLogout 
}: {
  session: Session;
  userProfile: UserProfile | null;
  setUserProfile: (profile: UserProfile | null) => void;
  currentView: string;
  setCurrentView: (view: string) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  currentSessionId: string | null;
  handleLogout: () => void;
}) {
  // Idle timeout - auto logout after 10 minutes of inactivity
  const { remainingSeconds, resetTimer } = useIdleTimeout({
    timeoutMinutes: 10,
    onTimeout: handleLogout,
    enabled: true,
  });
  const { isLoading: roleLoading, isManagement, canAccessView, getDefaultView } = useRole();

  // Trigger expiry alerts check for management users on load
  useEffect(() => {
    if (!isManagement) return;
    const checkExpiry = async () => {
      try {
        await supabase.functions.invoke('check-expiry-alerts');
      } catch (err) {
        console.error('Expiry check failed:', err);
      }
    };
    checkExpiry();
  }, [isManagement]);

  // Route protection: Redirect users to their default view if they can't access current view
  useEffect(() => {
    if (roleLoading) return;
    
    if (!canAccessView(currentView)) {
      setCurrentView(getDefaultView());
    }
  }, [roleLoading, currentView, canAccessView, getDefaultView, setCurrentView]);

  // Scroll to top when switching views
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentView]);

  return (
    <div className="min-h-screen bg-background flex w-full items-stretch">
      <Suspense fallback={<div className="w-16 min-h-screen bg-sidebar-background self-stretch" />}>
        <AppSidebar
          user={userProfile}
          currentView={currentView}
          onNavigate={setCurrentView}
          onLogout={handleLogout}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          remainingSeconds={remainingSeconds}
        />
      </Suspense>

      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar with search */}
        <div className="flex-shrink-0 h-12 border-b border-border/30 bg-card/30 backdrop-blur-sm flex items-center justify-between px-4 md:px-6 z-10">
          <div className="flex-1" />
          <Suspense fallback={null}>
            <CommandPalette onNavigate={setCurrentView} />
          </Suspense>
        </div>
        <main className="flex-1 overflow-auto">
            <div className={currentView === 'vacation' || currentView === 'approvals' || currentView === 'company-info' || currentView === 'portals' || currentView === 'activity-log' || currentView === 'monthly-po' || currentView === 'timesheets' || currentView === 'daily-tasks' || currentView === 'self-service' || currentView === 'shifts' || currentView === 'announcements' || currentView === 'nurse-docs' || currentView === 'doctor-docs' || currentView === 'driver-docs' || currentView === 'reports' || currentView === 'procurement' || currentView === 'warehouse' || currentView === 'sales' || currentView === 'hr' ? '' : 'p-8 lg:p-10 max-w-7xl mx-auto'}>
            {/* Breadcrumb Navigation */}
              {currentView !== 'dashboard' && currentView !== 'vacation' && (
              <div className={currentView === 'approvals' || currentView === 'company-info' || currentView === 'portals' || currentView === 'activity-log' || currentView === 'monthly-po' || currentView === 'timesheets' || currentView === 'daily-tasks' || currentView === 'self-service' || currentView === 'shifts' || currentView === 'announcements' || currentView === 'nurse-docs' || currentView === 'doctor-docs' || currentView === 'driver-docs' || currentView === 'reports' || currentView === 'procurement' || currentView === 'warehouse' || currentView === 'sales' || currentView === 'hr' ? 'px-8 pt-6' : ''}>
                <Suspense fallback={null}>
                  <PageBreadcrumb 
                    currentView={currentView} 
                    onNavigate={setCurrentView}
                    defaultView={getDefaultView()}
                  />
                </Suspense>
              </div>
            )}
            <Suspense fallback={<PageLoader />}>
              <Suspense fallback={null}>
                <AnimatePresence mode="wait">
                {currentView === 'dashboard' && canAccessView('dashboard') && (
                  <PageTransition key="dashboard">
                    <Dashboard />
                  </PageTransition>
                )}
                {currentView === 'operations' && canAccessView('operations') && (
                  <PageTransition key="operations">
                    <OperationsCenter />
                  </PageTransition>
                )}
                {currentView === 'daily-tasks' && canAccessView('daily-tasks') && (
                  <PageTransition key="daily-tasks">
                    <DailyTasksPage />
                  </PageTransition>
                )}
                {currentView === 'monthly-po' && canAccessView('monthly-po') && (
                  <PageTransition key="monthly-po">
                    <MonthlyPOPage />
                  </PageTransition>
                )}
                {currentView === 'fleet-tracker' && canAccessView('fleet-tracker') && (
                  <PageTransition key="fleet-tracker">
                    <FleetModule />
                  </PageTransition>
                )}
                {currentView === 'fleet' && canAccessView('fleet') && (
                  <PageTransition key="fleet">
                    <FleetCommandPage />
                  </PageTransition>
                )}
                {currentView === 'messenger' && canAccessView('messenger') && (
                  <PageTransition key="messenger">
                    <MessengerPage 
                      userId={session.user.id} 
                      userProfile={userProfile}
                    />
                  </PageTransition>
                )}
                {currentView === 'vacation' && canAccessView('vacation') && (
                  <PageTransition key="vacation">
                    <VacationPage userId={session.user.id} />
                  </PageTransition>
                )}
                {currentView === 'approvals' && canAccessView('approvals') && (
                  <PageTransition key="approvals">
                    <LeaveApprovalsPage managerId={session.user.id} />
                  </PageTransition>
                )}
                {currentView === 'company-info' && canAccessView('company-info') && (
                  <PageTransition key="company-info">
                    <CompanyDocsModule />
                  </PageTransition>
                )}
                {currentView === 'portals' && canAccessView('portals') && (
                  <PageTransition key="portals">
                    <PortalUsersModule />
                  </PageTransition>
                )}
                {currentView === 'activity-log' && canAccessView('activity-log') && (
                  <PageTransition key="activity-log">
                    <ActivityLogPage />
                  </PageTransition>
                )}
                {currentView === 'timesheets' && canAccessView('timesheets') && (
                  <PageTransition key="timesheets">
                    <TimesheetsPage />
                  </PageTransition>
                )}
                {currentView === 'self-service' && canAccessView('self-service') && (
                  <PageTransition key="self-service">
                    <EmployeeSelfServicePage 
                      userId={session.user.id}
                      userProfile={userProfile}
                    />
                  </PageTransition>
                )}
                {currentView === 'shifts' && canAccessView('shifts') && (
                  <PageTransition key="shifts">
                    <ShiftCalendar userId={session.user.id} />
                  </PageTransition>
                )}
                {currentView === 'announcements' && canAccessView('announcements') && (
                  <PageTransition key="announcements">
                    <div className="p-4 md:p-6 lg:p-8">
                      <AnnouncementBoard />
                    </div>
                  </PageTransition>
                )}
                {currentView === 'nurse-docs' && canAccessView('nurse-docs') && (
                  <PageTransition key="nurse-docs">
                    <NurseDocumentsManagement />
                  </PageTransition>
                )}
                {currentView === 'doctor-docs' && canAccessView('doctor-docs') && (
                  <PageTransition key="doctor-docs">
                    <DoctorDocumentsManagement />
                  </PageTransition>
                )}
                {currentView === 'driver-docs' && canAccessView('driver-docs') && (
                  <PageTransition key="driver-docs">
                    <DriverDocumentsManagement />
                  </PageTransition>
                )}
                {currentView === 'reports' && canAccessView('reports') && (
                  <PageTransition key="reports">
                    <ReportsPage />
                  </PageTransition>
                )}
                {currentView === 'procurement' && canAccessView('procurement') && (
                  <PageTransition key="procurement">
                    <ProcurementPage />
                  </PageTransition>
                )}
                {currentView === 'warehouse' && canAccessView('warehouse') && (
                  <PageTransition key="warehouse">
                    <WarehousePage />
                  </PageTransition>
                )}
                {currentView === 'sales' && canAccessView('sales') && (
                  <PageTransition key="sales">
                    <SalesPage />
                  </PageTransition>
                )}
                {currentView === 'hr' && canAccessView('hr') && (
                  <PageTransition key="hr">
                    <HRPage />
                  </PageTransition>
                )}
                {currentView === 'profile' && (
                  <PageTransition key="profile">
                    <div className="p-8 lg:p-10 max-w-7xl mx-auto">
                      <UserProfileView 
                        user={userProfile}
                        currentSessionId={currentSessionId || undefined}
                        onProfileUpdate={(updatedProfile) => setUserProfile(updatedProfile)} 
                      />
                    </div>
                  </PageTransition>
                )}
                </AnimatePresence>
              </Suspense>
            </Suspense>
          </div>
        </main>
        
        <footer className="bg-card/50 backdrop-blur-sm border-t border-border/30 py-5 px-8">
          <div className="flex items-center justify-center gap-4">
            <p className="text-sm text-muted-foreground font-medium tracking-wide">
              MirzaERP v2.1 <span className="text-primary">•</span> Enterprise Excellence
            </p>
            <SoundToggle />
          </div>
        </footer>
      </div>

      {/* AI Help Desk Widget - only render if user preference allows */}
      {userProfile?.show_ai_assistant !== false && (
        <Suspense fallback={null}>
          <ChatWidget />
        </Suspense>
      )}
      
      {/* Scroll to Top Button */}
      <Suspense fallback={null}>
        <ScrollToTop threshold={400} />
      </Suspense>

      {/* Session Expiry Warning Modal */}
      <SessionExpiryWarning
        remainingSeconds={remainingSeconds}
        warningThreshold={60}
        onStayLoggedIn={resetTimer}
        onLogout={handleLogout}
      />
    </div>
  );
}

function App() {
  type TwoFactorChallenge = {
    required: boolean;
    userId: string | null;
    email: string;
    /** We no longer store the plaintext password in React state for security */
  };

  const [session, setSession] = useState<Session | null>(null);
  const [currentView, setCurrentViewState] = useState(() => {
    const viewFromUrl = getViewFromPath(window.location.pathname);
    return viewFromUrl || 'dashboard';
  });
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  // Sync URL with currentView - update browser history
  const setCurrentView = useCallback((view: string) => {
    setCurrentViewState(view);
    const newPath = view === 'dashboard' ? '/' : `/${view}`;
    if (window.location.pathname !== newPath) {
      window.history.pushState({ view }, '', newPath);
    }
  }, []);

  // Listen for browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const viewFromUrl = getViewFromPath(window.location.pathname);
      if (viewFromUrl) {
        setCurrentViewState(viewFromUrl);
      } else {
        setCurrentViewState('dashboard');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarCollapsed(true);
    }
  }, [isMobile]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [pending2FACheck, setPending2FACheck] = useState(false);
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<TwoFactorChallenge>({
    required: false,
    userId: null,
    email: '',
  });

  // Session security - clears session when tab is closed
  const { markSessionActive } = useSessionSecurity();

  const requireTwoFactor = (params: { userId: string; email: string }) => {
    setPending2FACheck(true);
    setTwoFactorChallenge({
      required: true,
      userId: params.userId,
      email: params.email,
    });
  };

  const startPending2FACheck = () => {
    setPending2FACheck(true);
  };

  const clearTwoFactor = () => {
    setPending2FACheck(false);
    setTwoFactorChallenge({ required: false, userId: null, email: '' });
  };

  const createUserSession = async () => {
    try {
      const response = await supabase.functions.invoke('manage-session', {
        body: {
          action: 'create',
          deviceInfo: navigator.platform || 'Unknown Device',
          userAgent: navigator.userAgent
        }
      });
      if (response.data?.session?.id) {
        setCurrentSessionId(response.data.session.id);
        localStorage.setItem('currentSessionId', response.data.session.id);
      }
    } catch (error) {
      console.error('Failed to create session:', error);
    }
  };

  useEffect(() => {
    const storedSessionId = localStorage.getItem('currentSessionId');
    if (storedSessionId) {
      setCurrentSessionId(storedSessionId);
    }

    supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (session?.user) {
        // Mark session as active in sessionStorage (for tab-close detection)
        markSessionActive();
        setTimeout(() => fetchProfile(session.user.id), 0);
        if (event === 'SIGNED_IN' && !storedSessionId) {
          setTimeout(() => createUserSession(), 0);
        }
      } else {
        setUserProfile(null);
        setCurrentSessionId(null);
        localStorage.removeItem('currentSessionId');
        sessionStorage.removeItem('sessionActive');
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        markSessionActive();
        fetchProfile(session.user.id);
      }
      setLoading(false);
    });
  }, [markSessionActive]);

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    if (data) setUserProfile(data);
  };

  const handleLogout = async () => {
    clearTwoFactor();

    if (currentSessionId && session?.user) {
      await supabase.functions.invoke('manage-session', {
        body: { action: 'revoke', sessionId: currentSessionId }
      });
    }
    localStorage.removeItem('currentSessionId');
    await supabase.auth.signOut();
    setCurrentView('dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="relative">
          <div className="h-12 w-12 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          <div className="absolute inset-0 h-12 w-12 border-2 border-transparent border-r-accent rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
        </div>
      </div>
    );
  }

  const isResetPasswordRoute = window.location.pathname === '/reset-password' || 
    window.location.hash.includes('type=recovery');

  if (isResetPasswordRoute) {
    return (
      <ThemeProvider defaultTheme="dark" storageKey="nexus-ui-theme">
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <Suspense fallback={null}>
              <FilmGrain />
              <CustomCursor />
            </Suspense>
            <Suspense fallback={<PageLoader />}>
              <ResetPassword />
            </Suspense>
          </TooltipProvider>
        </LanguageProvider>
      </ThemeProvider>
    );
  }

  // Public pages (accessible with or without auth)
  const publicRoute = window.location.pathname;
  
  if (publicRoute === '/about') {
    return (
      <ThemeProvider defaultTheme="dark" storageKey="nexus-ui-theme">
        <LanguageProvider>
          <SoundProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Suspense fallback={null}>
                <FilmGrain />
                <CustomCursor />
              </Suspense>
              <Suspense fallback={<PageLoader />}>
                <AboutPage />
              </Suspense>
            </TooltipProvider>
          </SoundProvider>
        </LanguageProvider>
      </ThemeProvider>
    );
  }

  if (publicRoute === '/gallery') {
    return (
      <ThemeProvider defaultTheme="dark" storageKey="nexus-ui-theme">
        <LanguageProvider>
          <SoundProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Suspense fallback={null}>
                <FilmGrain />
                <CustomCursor />
              </Suspense>
              <Suspense fallback={<PageLoader />}>
                <GalleryPage />
              </Suspense>
            </TooltipProvider>
          </SoundProvider>
        </LanguageProvider>
      </ThemeProvider>
    );
  }

  if (publicRoute === '/services') {
    return (
      <ThemeProvider defaultTheme="dark" storageKey="nexus-ui-theme">
        <LanguageProvider>
          <SoundProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Suspense fallback={null}>
                <FilmGrain />
                <CustomCursor />
              </Suspense>
              <Suspense fallback={<PageLoader />}>
                <ServicesPage />
              </Suspense>
            </TooltipProvider>
          </SoundProvider>
        </LanguageProvider>
      </ThemeProvider>
    );
  }

  if (publicRoute === '/contact') {
    return (
      <ThemeProvider defaultTheme="dark" storageKey="nexus-ui-theme">
        <LanguageProvider>
          <SoundProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Suspense fallback={null}>
                <FilmGrain />
                <CustomCursor />
              </Suspense>
              <Suspense fallback={<PageLoader />}>
                <ContactPage />
              </Suspense>
            </TooltipProvider>
          </SoundProvider>
        </LanguageProvider>
      </ThemeProvider>
    );
  }

  // Login page route - show AuthPage for unauthenticated users
  if (publicRoute === '/login' && (!session || twoFactorChallenge.required || pending2FACheck)) {
    return (
      <ThemeProvider defaultTheme="dark" storageKey="nexus-ui-theme">
        <LanguageProvider>
          <SoundProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Suspense fallback={null}>
                <FilmGrain />
                <CustomCursor />
              </Suspense>
              <Suspense fallback={<PageLoader />}>
                <AuthPage 
                  onLogin={() => setPending2FACheck(false)}
                  twoFactorChallenge={twoFactorChallenge}
                  onRequireTwoFactor={requireTwoFactor}
                  onCancelTwoFactor={clearTwoFactor}
                  onStartPending2FACheck={startPending2FACheck}
                />
              </Suspense>
            </TooltipProvider>
          </SoundProvider>
        </LanguageProvider>
      </ThemeProvider>
    );
  }

  if (!session && (publicRoute === '/' || publicRoute === '/home') && !twoFactorChallenge.required && !pending2FACheck) {
    return (
      <ThemeProvider defaultTheme="dark" storageKey="nexus-ui-theme">
        <LanguageProvider>
          <SoundProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Suspense fallback={null}>
                <FilmGrain />
                <CustomCursor />
              </Suspense>
              <Suspense fallback={<PageLoader />}>
                <HomePage />
              </Suspense>
            </TooltipProvider>
          </SoundProvider>
        </LanguageProvider>
      </ThemeProvider>
    );
  }

  if (!session || twoFactorChallenge.required || pending2FACheck) {
    return (
      <ThemeProvider defaultTheme="dark" storageKey="nexus-ui-theme">
        <LanguageProvider>
          <SoundProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Suspense fallback={null}>
                <FilmGrain />
                <CustomCursor />
              </Suspense>
              <Suspense fallback={<PageLoader />}>
                <AuthPage 
                  onLogin={() => setPending2FACheck(false)}
                  twoFactorChallenge={twoFactorChallenge}
                  onRequireTwoFactor={requireTwoFactor}
                  onCancelTwoFactor={clearTwoFactor}
                  onStartPending2FACheck={startPending2FACheck}
                />
              </Suspense>
            </TooltipProvider>
          </SoundProvider>
        </LanguageProvider>
      </ThemeProvider>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark" storageKey="nexus-ui-theme">
        <LanguageProvider>
          <SoundProvider>
            <RoleProvider user={session.user}>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <Suspense fallback={null}>
                  <FilmGrain />
                  <CustomCursor />
                </Suspense>
                
                <AppContent
                  session={session}
                  userProfile={userProfile}
                  setUserProfile={setUserProfile}
                  currentView={currentView}
                  setCurrentView={setCurrentView}
                  sidebarCollapsed={sidebarCollapsed}
                  setSidebarCollapsed={setSidebarCollapsed}
                  currentSessionId={currentSessionId}
                  handleLogout={handleLogout}
                />
              </TooltipProvider>
            </RoleProvider>
          </SoundProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
